import React from 'react';
import { Wrench, Printer, Sparkles, CheckCircle, Building2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Services = () => {
  const { t } = useLanguage();

  const services = [
    {
      key: 'metalwork',
      icon: Wrench,
      image: 'https://images.unsplash.com/photo-1714504904786-b6732390b206',
    },
    {
      key: 'print3d',
      icon: Printer,
      image: 'https://images.unsplash.com/photo-1752614672115-adcf3e2596af',
    },
    {
      key: 'engraving',
      icon: Sparkles,
      image: 'https://images.unsplash.com/photo-1739850269293-8ed949c32b22',
    },
    {
      key: 'scaffolding',
      icon: Building2,
      image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white dark:from-black dark:via-gray-900 dark:to-black transition-colors duration-200 pt-32 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">{t('services.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">{t('services.subtitle')}</p>
          <div className="w-24 h-1 bg-green-600 dark:bg-green-500 mx-auto mt-6"></div>
        </div>

        {/* Services */}
        <div className="space-y-20">
          {services.map((service, index) => {
            const Icon = service.icon;
            const isEven = index % 2 === 0;
            const uses = t(`services.items.${service.key}.uses`);
            const usesArray = Array.isArray(uses) ? uses : [];

            return (
              <div
                key={service.key}
                data-testid={`service-section-${service.key}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}
              >
                {/* Image */}
                <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                  <div className="relative rounded-xl overflow-hidden group shadow-2xl">
                    <img
                      src={service.image}
                      alt={t(`services.items.${service.key}.title`)}
                      className="w-full h-[400px] object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 w-16 h-16 bg-green-500 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30">
                      <Icon size={32} className="text-white dark:text-black" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    {t(`services.items.${service.key}.title`)}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-6">
                    {t(`services.items.${service.key}.description`)}
                  </p>

                  <div className="space-y-3">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                      {t('services.usesTitle')}
                    </h3>
                    {usesArray.map((use, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <CheckCircle size={20} className="text-green-600 dark:text-green-400 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{use}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-20 text-center bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-12 shadow-xl">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            {t('services.ctaTitle')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-8">
            {t('services.ctaText')}
          </p>
          <a
            href="/kontakt"
            data-testid="services-cta-btn"
            className="inline-block bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white dark:text-black font-semibold px-8 py-4 rounded-lg transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            {t('services.ctaButton')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default Services;
